import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useFactionsAuthedApi } from '@/features/wallet';
import { fetchSpeedJobs } from '@/lib/api/speed';
import { queryKeys } from '@/lib/query';

import { isActiveSpeedJob } from '../jobStatus';

import { useSpeedJobObserver } from './useSpeedJobObserver';

export { isActiveSpeedJob } from '../jobStatus';

const JOBS_LIMIT = 10;
const ACTIVE_REFETCH_INTERVAL_MS = 3_000;
const IDLE_REFETCH_INTERVAL_MS = 20_000;

// ---------------------------------------------------------------------------
// Exactly one caller polls.
//
// React Query dedupes CONCURRENT fetches of the same key, but `refetchInterval` is per-observer:
// every mounted caller runs its own timer, and staggered timers do not overlap, so they are not
// deduped. This hook is reached from about eight places (CustodialPanelContainer directly, plus
// useAgentReactivation via useSnappyWalletState from SpeedTradeRail, MobileControlPanel and the
// banners) — eight 3s timers, which is the ~2 requests/second observed against /speed/jobs.
//
// Note this is a DIFFERENT mechanism from the job observer's raw setInterval: fixing that one did
// not fix this, because this polling is react-query's own.
//
// One instance holds the lease and polls; the rest read the shared cache. Leadership is released
// on unmount and re-elected, so the poller surviving is not tied to any particular component.
// ---------------------------------------------------------------------------

let pollLeader: symbol | null = null;
const contenders = new Set<{ token: symbol; onElected: (isLeader: boolean) => void }>();

function electPollLeader(): void {
    if (pollLeader !== null) return;
    const next = contenders.values().next();
    if (next.done) return;
    pollLeader = next.value.token;
    next.value.onElected(true);
}

function joinPollElection(entry: { token: symbol; onElected: (isLeader: boolean) => void }): () => void {
    contenders.add(entry);
    electPollLeader();
    return () => {
        contenders.delete(entry);
        if (pollLeader === entry.token) {
            pollLeader = null;
            entry.onElected(false);
            // hand the timer straight to another mounted caller rather than leaving the tab
            // unpolled until something happens to remount.
            electPollLeader();
        }
    };
}

export function useCustodialJobs(enabled = true) {
    // jobs list moved to factions.backend's speed facet (read-only).
    const factionsApi = useFactionsAuthedApi();
    const tokenRef = useRef<symbol>(Symbol('speed-jobs-poller'));
    const [isPollLeader, setIsPollLeader] = useState(false);

    const active = enabled && !!factionsApi;
    useEffect(() => {
        if (!active) return;
        return joinPollElection({
            token: tokenRef.current,
            onElected: setIsPollLeader,
        });
    }, [active]);

    const query = useQuery({
        queryKey: queryKeys.speed.jobs(),
        queryFn: async ({ signal }) =>
            fetchSpeedJobs(factionsApi!, JOBS_LIMIT, signal),
        enabled: active,
        staleTime: 3_000,
        refetchInterval: (queryState) => {
            if (!isPollLeader) return false;
            const jobs = queryState.state.data?.jobs ?? [];
            return jobs.some(isActiveSpeedJob)
                ? ACTIVE_REFETCH_INTERVAL_MS
                : IDLE_REFETCH_INTERVAL_MS;
        },
    });
    const jobs = useMemo(() => query.data?.jobs ?? [], [query.data?.jobs]);
    // one observer per tab regardless of how many components read job state — it refcounts
    // internally and reads jobs from this same query cache.
    useSpeedJobObserver(active);

    return useMemo(() => {
        const activeJobs = jobs.filter(isActiveSpeedJob);
        return {
            jobs,
            activeJobs,
            hasActiveJobs: activeJobs.length > 0,
            isLoading: query.isLoading,
            isFetching: query.isFetching,
            refetch: query.refetch,
        };
    }, [jobs, query.isFetching, query.isLoading, query.refetch]);
}

/** test-only: whether some mounted caller currently owns the polling timer. */
export function hasCustodialJobsPollLeader(): boolean {
    return pollLeader !== null;
}

/** test-only: clears the polling lease so a suite starts from a known state. */
export function resetCustodialJobsPollLeader(): void {
    pollLeader = null;
    contenders.clear();
}
